import React from "react";
import { computed, flow, makeObservable, observable, reaction } from "mobx";
import { disposeOnUnmount, inject, observer } from "mobx-react";
import { DateTime } from "luxon";

import { getValueFromBlock } from "@utilifeed/config/blocks";
import { have_blocks, isValue } from "@core/utils";

class RollingTwelveTrendManager extends React.Component {
  dataAvailable = false;
  dataProcessingDone = false;
  dataError = false;
  errorMessage = "";

  constructor(props) {
    super(props);

    makeObservable(this, {
      errorMessage: true,
      getData: true,
      componentDidMount: true,
      lastProcessedMonth: true,
      processData: true,
      render: true,
      dataAvailable: observable,
      dataProcessingDone: observable,
      dataError: observable,
      requiredMonths: computed,
      blocks_required: computed,
    });

    this.data = null;
    this.pdata = null;
    this.getData = this.getData.bind(this);
  }

  getData = flow(function* () {
    const { rootStore, networks, resource_id, resource_type } = this.props;
    if (resource_id) {
      this.dataAvailable = false;
      this.dataProcessingDone = false;
      const subdfs = yield rootStore.newapi.getInfoBlocksV4({
        resource_type,
        resource_id,
        network_id: networks.current_network.uid,
        block_names: this.blocks_required,
      });
      this.data = subdfs;
      if (have_blocks(subdfs, this.blocks_required)) {
        this.dataAvailable = true;
        this.dataError = false;
      } else {
        this.dataError = true;
        this.errorMessage = "No data available";
        // Which blocks are missing?
        const missingBlocks = this.blocks_required.filter((blockName) => !subdfs[blockName]);
        console.error("Missing blocks:", missingBlocks);
      }
    } else {
      this.dataAvailable = false;
      this.dataProcessingDone = false;
      this.dataError = true;
      this.errorMessage = "No resource id provided";
      console.error("No resource id provided");
    }
  });

  componentDidMount() {
    const { networks } = this.props;
    disposeOnUnmount(
      this,
      reaction(() => [this.requiredMonths, networks.ready], this.getData)
    );
    disposeOnUnmount(
      this,
      reaction(
        () => this.dataAvailable,
        () => {
          if (this.dataAvailable) {
            this.processData();
          }
        }
      )
    );
  }

  get lastProcessedMonth() {
    const { networks, resource_type } = this.props;
    let [year, month] = networks.networkActiveEPMonth || networks.networkActiveMonth;
    if (resource_type === "cluster" && networks.networkActiveEPMonthClusters) {
      [year, month] = networks.networkActiveEPMonthClusters;
    }
    return DateTime.fromObject({ year, month, day: 5 });
  }

  get requiredMonths() {
    const reqMonths = [];
    [0, 12].forEach((monthsBack) => {
      const reqMonth = this.lastProcessedMonth.plus({ months: -1 * monthsBack });
      reqMonths.push(reqMonth);
    });
    return reqMonths;
  }

  get blocks_required() {
    const { block_name_gen } = this.props;
    const blockNames = [];

    this.requiredMonths.forEach((month) => {
      blockNames.push(block_name_gen(month));
    });
    return blockNames;
  }

  processData = () => {
    const { resource_id, block_name_gen, column_name } = this.props;
    const reading = { current: 0, previous: 0 };
    [0, 12].forEach((monthsBack) => {
      const blockMonth = this.lastProcessedMonth.plus({ months: -1 * monthsBack });
      const block = block_name_gen(blockMonth);
      const val = getValueFromBlock(this.data, block, resource_id, column_name);
      if (isValue(val)) {
        if (monthsBack < 12) {
          reading.current += val;
        } else {
          reading.previous += val;
        }
      }
    });
    this.pdata = reading;
    this.dataProcessingDone = true;
  };

  render() {
    const { UiComp, trendtype = "pct", uiprops = {}, yUnit } = this.props;
    const value = this.dataProcessingDone ? this.pdata.current : 0;
    const trendValue = !this.dataProcessingDone
      ? 0
      : trendtype === "pct"
        ? ((this.pdata.current - this.pdata.previous) / this.pdata.previous) * 100
        : this.pdata.current - this.pdata.previous;

    // titleAddon prop from NormalizedYearCardManager
    const additionalProps = {};
    if (uiprops.titleAddon !== undefined) {
      additionalProps.titleAddon = uiprops.titleAddon;
    }
    if (uiprops.testId !== undefined) {
      additionalProps.testId = uiprops.testId;
    }

    return (
      <UiComp
        haveData={this.dataProcessingDone}
        loading={!this.dataProcessingDone}
        error={this.dataError}
        errorMessage={this.errorMessage}
        value={value}
        yUnit={yUnit}
        trend={{
          value: trendValue,
          title: uiprops.deltaTooltip,
          suffix: uiprops.trendSuffix,
        }}
        {...uiprops}
        {...additionalProps}
      />
    );
  }
}

export default inject("rootStore", "networks")(observer(RollingTwelveTrendManager));
